/**
 * A React component that displays a Hero Carousel and integrates functionality from the Embla Carousel library.
 * It supports autoplay functionality and makes use of Embla Carousel hooks to manage the carousel behavior.
 * The component listens to the window resize event to update the browser width and handles click events for navigating through the carousel items.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.sanity - The sanity object containing data related to the hero and banner lists.
 * @param {Object[]} props.sanity.data[0].heroList - Array of hero objects to be displayed in the carousel.
 * @param {Object[]} props.sanity.data[0].bannerList - Array of banner objects to be displayed in the carousel.
 *
 * @returns {React.Element} The JSX for rendering the Hero Carousel section.
 *
 * @example
 * ```javascript
 * <HeroCarousel sanity={sanityData} />
 * ```
 *
 * @function
 * @name HeroCarousel
 */
import React, {useCallback, useEffect, useState} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import '../../assets/css/carousel.css';
import '../../assets/css/custom.css';
import {Image, Link} from '@shopify/hydrogen';
import {PortableText} from '@portabletext/react';
import {getPath} from '../../lib/methods';
import { isMobile } from '../../lib/Utils';

export default function HeroCarousel({sanity}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [browserWidth, setBrowserWidth] = useState(0);
  const options = {}; // Options
  const autoplayRoot = (viewportRef) => viewportRef.parentElement; // Root node
  const autoplay = Autoplay(options, autoplayRoot);

  const [viewportRef, emblaRef] = useEmblaCarousel(
    {loop: false, dragFree: false, draggable: true},
    [autoplay],
  );

  const [emblaRefBanner] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    selectedClass: '',
    dragFree: true,
  });

  const hero = sanity ? sanity.data[0].heroList : [];
  const banner = sanity ? sanity.data[0].bannerList : [];
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const scrollTo = useCallback(
    (index) => emblaRef && emblaRef.scrollTo(index),
    [emblaRef],
  );

  const updateDimensions = () => {
    const width = window.innerWidth;
    setBrowserWidth(width);
  };

  const onSelect = useCallback(() => {
    if (!emblaRef) return;
    setSelectedIndex(emblaRef.selectedScrollSnap());
    setPrevBtnEnabled(emblaRef.canScrollPrev());
    setNextBtnEnabled(emblaRef.canScrollNext());
  }, [emblaRef, setSelectedIndex]);

  useEffect(() => {
    if (!emblaRef) return;
    onSelect();
    setScrollSnaps(emblaRef.scrollSnapList());
    emblaRef.on('select', onSelect);
  }, [emblaRef, setScrollSnaps, onSelect]);

  useEffect(() => {
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const responsive = {
    isHeroMobile: (item) => {
      return browserWidth < 1023
        ? {
            height: '270px',
            // background: `${
            //   item?.backgroundImageMobile
            //     ? `url("${item.backgroundImageMobile.asset.url}")`
            //     : ''
            // }`,
            // backgroundRepeat: 'no-repeat',
            // backgroundSize: 'cover',
            image: item?.backgroundImageMobile.asset.url,
          }
        : {
            minHeight: '347px',
            height: '487px',
            // backgroundRepeat: 'no-repeat',
            // background: `${
            //   item?.backgroundImageDesktop
            //     ? `url("${item.backgroundImageDesktop.asset.url+`?h=430`}")`
            //     : ''
            // }`,
            // backgroundSize: 'cover',
            image: item?.backgroundImageDesktop.asset.url,
          };
    },
  };

  const checkAndGoTolink = (item) => {
    if (item.hasButton) {
      window.location.href = getPath(item);
    } else if (item.redirectionType) {
      window.location.href = getPath(item);
    }
  };

  const responsiveImage = {
    isHeroMobile: (item, paramsPhone, imageDesktop) => {
      return browserWidth < 576
        ? item+'?'+paramsPhone
        : (imageDesktop) ? imageDesktop :item
    },
  };
  /**
   * Returns the JSX to render a Hero Carousel section.
   *
   * The top part of the section consists of a carousel with slides containing hero images.
   * Each slide may contain a title, an optional button, promotional text, and a featured image.
   * The title text can be a mix of normal text and PortableText components.
   * The promotional part includes a static text "Envíos gratis en todo México desde $499 pesos".
   *
   * Below the hero carousel, if there are more than one hero, a series of navigational dots is displayed to represent each slide,
   * with the currently selected one being larger and fully opaque.
   *
   * Following the navigational dots, there may be an additional banner carousel.
   * This consists of a series of slides, each with a title, background image, and optional featured image.
   *
   * @returns React.Element The JSX for rendering the Hero Carousel section, including the hero slides, navigational dots, and banner slides.
   *
   * @example
   * ```jsx
   * return (
   *    <section className="section_librero pb-8 section_home_carousel">
   *      { JSX for rendering the Hero Carousel component
   *    </section>
   * );
   * ```
   */
  return (
    <section className="section_librero pb-8 section_home_carousel">
      <div className="embla" ref={viewportRef}>
        <div className="embla__container librero_carousel"
          style={{display: 'flex'}}>
          {hero
            ? hero.map((item) => {
                return (
                  <div className="embla__slide " key={item._key}>
                    <Link className="carousel_link" to={getPath(item)}>
                      <div
                        className=" justify-center hero_slide w-full relative" 
                        /* style={responsive.isHeroMobile(item)} */
                      >
                        <img src={
                          responsiveImage.isHeroMobile(
                              item.backgroundImageMobile.asset.url,
                              'fm=webp&w=425',
                              item.backgroundImageDesktop.asset.url
                          )
                        }
                          className='top-0 left-0 z-0'
                          fetchpriority="eager"
                          alt={`Hero Image ${item._key}`}
                          loading="lazy"
                          decoding="async"
                        />

                        <div className="container">
                          <div
                            className={`relative w-full m-auto flex justify-center h-full 
                                       ${
                                         item.hasButton || item.redirectionType
                                           ? 'cursor-pointer'
                                           : ''
                                       }`}
                          >
                            <div className="hero_slide_title xs:items-start xs:justify-end absolute md:w-4/12 left-0 h-full xs:w-full flex flex-col justify-center justify-items-center items-left items-start">
                              <div
                                className={`slide_title xs:absolute xs:top-8 md:text-5xl
                              ${
                                item.darkText ? 'text-black-100' : 'text-white'
                              } 
                           `}
                              >
                                {item?.title
                                  ? Array.isArray(item.title)
                                    ? item.title.map((block) => {
                                        return (
                                          <div key={block._key}>
                                            {block._type !== 'block' ||
                                            !block.children ? (
                                              ''
                                            ) : (
                                              <PortableText value={block} />
                                            )}
                                          </div>
                                        );
                                      })
                                    : ''
                                  : item.title}
                              </div>
                              {item.hasButton && (
                                <>
                                  <Link
                                    className="btn-main xs:mx-auto xs:mb-14 mt-10 inline-flex items-center bg-purple-120 text-white font-semibold rounded-full px-10 py-2 w-auto xs:py-4"
                                    to={getPath(item)}
                                  >
                                    {item.buttonText}
                                  </Link>
                                </>
                              )}
                            </div>
                            <div className="hero_promo absolute right-0 top-10 xs:hidden">
                              <div className="relative hero_promo py-2 px-4 bg-purple-120 text-white font-normal rounded-full flex justify-center gap-4 content-center items-center">
                                <svg
                                  x="0px"
                                  y="0px"
                                  viewBox="0 0 25 18"
                                  fill="#ffffff"
                                  width="25px"
                                  height="18px"
                                >
                                  <g>
                                    <path
                                      className="st0"
                                      d="M17.4,14.8H7.5c-0.2,0-0.3-0.1-0.3-0.3v-0.1c-0.1-0.1-0.1-0.1-0.1-0.2c0-0.1,0.1-0.3,0.2-0.3c0,0,0,0,0.1,0
		h9.3V6.4c0.1-1.5-0.3-3-1.3-4.1C14.7,1.5,13.7,1,12.6,1H1.9v12.8H3c0.2,0,0.3,0.1,0.3,0.3c0,0.1,0,0.2-0.1,0.2v0.1
		c0,0.1-0.1,0.3-0.2,0.3c0,0,0,0-0.1,0H1.2c-0.2,0-0.3-0.1-0.3-0.3V0.3C0.9,0.2,1.1,0,1.2,0h11.4c1.3,0,2.6,0.6,3.5,1.5
		c1.1,1.3,1.7,3.1,1.6,4.8v8.1C17.7,14.7,17.5,14.8,17.4,14.8z"
                                    />
                                    <path
                                      className="st0"
                                      d="M5.8,17.8c-1.6-0.1-2.8-1.4-2.8-3v-0.4c0-0.1,0-0.3,0.1-0.4c0.3-1.3,1.4-2.2,2.7-2.3c1.3,0,2.5,1,2.7,2.3
		c0,0.1,0.1,0.3,0.1,0.4v0.4C8.7,16.4,7.4,17.7,5.8,17.8z M5.8,12.5c-1,0-1.9,0.7-2.1,1.7c0,0.1,0,0.2-0.1,0.3v0.3
		c0,1.2,0.9,2.2,2.1,2.3c1.2,0,2.1-1,2.1-2.2c0,0,0-0.1,0-0.1v-0.3l0,0c0-0.1,0-0.2-0.1-0.3C7.6,13.2,6.8,12.5,5.8,12.5z M5.8,16.4
		c-0.9,0-1.5-0.7-1.5-1.6v-0.4c0-0.2,0.2-0.3,0.4-0.2s0.3,0.2,0.2,0.4v0.2c0,0.5,0.3,0.9,0.8,0.9c0.5,0,0.8-0.4,0.8-0.8
		c0,0,0,0,0-0.1v-0.2c0-0.2,0.1-0.4,0.2-0.4c0.2,0,0.3,0.1,0.4,0.2v0.4c0.2,0.7-0.3,1.4-1,1.6C6,16.4,5.9,16.4,5.8,16.4z"
                                    />
                                    <path
                                      className="st0"
                                      d="M5.8,18c-1.7-0.1-3-1.4-2.9-3.1c0,0,0-0.1,0-0.1c-0.1-1.7,1.1-3.1,2.8-3.2c0,0,0.1,0,0.1,0
		c1.7,0.1,3,1.4,2.9,3.1c0,0,0,0.1,0,0.1c0.1,1.7-1.1,3.1-2.8,3.2C5.9,18,5.8,18,5.8,18z M5.8,12.6c-1.2,0.1-2,1-2,2.2
		c0,1.2,0.8,2.1,2,2.2c1.2-0.1,2-1,2-2.2C7.8,13.6,7,12.7,5.8,12.6z"
                                    />
                                    <path
                                      className="st0"
                                      d="M6.9,14.8c-0.2,0-0.3-0.1-0.3-0.3c0-0.1,0-0.1-0.1-0.2c-0.1-0.3-0.4-0.4-0.7-0.4c-0.3,0-0.6,0.2-0.7,0.4
		C5,14.4,5,14.5,5,14.6c0,0.2-0.2,0.3-0.4,0.2s-0.3-0.2-0.2-0.4c0-0.2,0.1-0.3,0.2-0.4c0.2-0.5,0.7-0.8,1.3-0.8c0.5,0,1,0.3,1.3,0.8
		c0.1,0.1,0.1,0.3,0.2,0.4C7.3,14.6,7.2,14.8,6.9,14.8z"
                                    />
                                    <path
                                      className="st0"
                                      d="M5.8,16.6c-0.9,0-1.6-0.8-1.6-1.8c0,0,0,0,0,0c-0.1-0.9,0.6-1.7,1.6-1.8c0,0,0,0,0,0c0.9,0,1.6,0.8,1.6,1.8
		c0,0,0,0,0,0C7.5,15.7,6.8,16.5,5.8,16.6C5.8,16.6,5.8,16.6,5.8,16.6z M5.8,14.1c-0.4,0-0.7,0.3-0.7,0.7c0,0,0,0.1,0,0.1
		c-0.1,0.4,0.2,0.8,0.6,0.8c0.4,0.1,0.8-0.2,0.8-0.6c0-0.1,0-0.2,0-0.2C6.5,14.5,6.3,14.2,5.8,14.1C5.9,14.1,5.8,14.1,5.8,14.1z"
                                    />
                                    <path
                                      className="st0"
                                      d="M17.8,14.8h-0.6c-0.3,0-0.5-0.2-0.5-0.5s0.2-0.5,0.5-0.5h0.7c0.2,0,0.3,0.1,0.3,0.3c0,0.1,0,0.2-0.1,0.2v0.1
		C18.2,14.7,18,14.8,17.8,14.8z"
                                    />
                                    <path
                                      className="st0"
                                      d="M19.1,14.5h-0.2c-0.2,0-0.3-0.1-0.3-0.3s0.1-0.3,0.3-0.3h0.2c0.2,0,0.3,0.1,0.3,0.3S19.3,14.5,19.1,14.5z"
                                    />
                                    <path
                                      className="st0"
                                      d="M19,14.8h-0.2c-0.2,0-0.3-0.1-0.3-0.3s0.1-0.3,0.3-0.3l0,0H19c0.2,0,0.3,0.1,0.3,0.3S19.2,14.8,19,14.8z"
                                    />
                                    <path
                                      className="st0"
                                      d="M21.4,14.8h-0.2c-0.2,0-0.3-0.1-0.3-0.3s0.1-0.3,0.3-0.3l0,0h0.2c0.2,0,0.3,0.1,0.3,0.3S21.6,14.8,21.4,14.8z"
                                    />
                                    <path
                                      className="st0"
                                      d="M21.3,14.5h-0.2c-0.2,0-0.3-0.1-0.3-0.3s0.1-0.3,0.3-0.3h0.2c0.2,0,0.3,0.1,0.3,0.3S21.5,14.5,21.3,14.5z"
                                    />
                                    <path
                                      className="st0"
                                      d="M24.5,14.8h-2.1c-0.2,0-0.3-0.1-0.3-0.3v-0.1C22,14.3,22,14.2,22,14.1c0-0.1,0.1-0.3,0.2-0.3c0,0,0,0,0.1,0H24
		v-4c-0.3-4.4-2.5-5-3.1-5h-3.8c-0.2-0.1-0.3-0.3-0.3-0.5c0-0.1,0-0.3,0.1-0.4c0.1-0.1,0.2-0.2,0.3-0.2h3.6c0.7,0,1.4,0.3,2,0.8
		c1.5,1.2,2,3.5,2.2,5.2l0,0v4.6C25,14.6,24.8,14.8,24.5,14.8z"
                                    />
                                    <path
                                      className="st0"
                                      d="M24.5,10.7h-3.7c-0.3,0-0.5-0.2-0.5-0.4c0,0,0,0,0-0.1v-6c0-0.3,0.2-0.5,0.5-0.5s0.5,0.2,0.5,0.5v5.5h3.2
		c0.3,0,0.5,0.2,0.5,0.4c0,0,0,0,0,0.1C25,10.5,24.8,10.7,24.5,10.7z"
                                    />
                                    <path
                                      className="st0"
                                      d="M20.1,17.8c-1.6-0.1-2.8-1.4-2.8-3v-0.4c0-0.1,0-0.3,0.1-0.4c0.3-1.3,1.4-2.2,2.7-2.3c1.3,0,2.5,1,2.7,2.3
		c0,0.1,0.1,0.3,0.1,0.4v0.4c0.1,1.6-1.1,2.9-2.7,3C20.2,17.8,20.1,17.8,20.1,17.8z M20.1,12.5c-1,0-1.9,0.7-2.1,1.7
		c0,0.1,0,0.2-0.1,0.3v0.3c0,1.2,0.9,2.2,2.1,2.3c1.2,0,2.1-1,2.1-2.2c0,0,0-0.1,0-0.1v-0.3l0,0c0-0.1,0-0.2-0.1-0.3
		C21.9,13.2,21.1,12.4,20.1,12.5C20.1,12.5,20.1,12.5,20.1,12.5z M20.1,16.4c-0.9,0-1.5-0.7-1.5-1.6v-0.4c0-0.2,0.2-0.3,0.4-0.2
		s0.3,0.2,0.2,0.4v0.2c0,0.5,0.3,0.9,0.8,0.9c0.5,0,0.8-0.4,0.8-0.8c0,0,0,0,0-0.1v-0.2c0-0.2,0.1-0.4,0.2-0.4
		c0.2,0,0.3,0.1,0.4,0.2v0.4c0.2,0.7-0.3,1.4-1,1.6C20.3,16.4,20.2,16.4,20.1,16.4z"
                                    />
                                    <path
                                      className="st0"
                                      d="M20.1,18c-1.7-0.1-3-1.4-2.9-3.1c0,0,0-0.1,0-0.1c-0.1-1.7,1.1-3.1,2.8-3.2c0,0,0.1,0,0.1,0
		c1.7,0.1,3,1.4,2.9,3.1c0,0,0,0.1,0,0.1c0.1,1.7-1.1,3.1-2.8,3.2C20.2,18,20.1,18,20.1,18z M20.1,12.6c-1.2,0.1-2,1-2,2.2
		c0,1.2,0.8,2.1,2,2.2c1.2-0.1,2-1,2-2.2C22.1,13.6,21.3,12.7,20.1,12.6z"
                                    />
                                    <path
                                      className="st0"
                                      d="M21.2,14.8c-0.2,0-0.3-0.1-0.3-0.3c0-0.1,0-0.1-0.1-0.2c-0.1-0.3-0.4-0.4-0.7-0.4c-0.3,0-0.6,0.2-0.7,0.4
		c0,0.1-0.1,0.2-0.1,0.3s-0.2,0.3-0.4,0.2s-0.3-0.2-0.2-0.4c0-0.2,0.1-0.3,0.2-0.4c0.2-0.5,0.7-0.8,1.3-0.8c0.5,0,1,0.3,1.3,0.8
		c0.1,0.1,0.1,0.3,0.2,0.4C21.6,14.6,21.5,14.8,21.2,14.8z"
                                    />
                                    <path
                                      className="st0"
                                      d="M20.1,16.6c-0.9,0-1.6-0.8-1.6-1.8c0,0,0,0,0,0c-0.1-0.9,0.5-1.7,1.4-1.8c0.9-0.1,1.7,0.5,1.8,1.4
		c0,0.1,0,0.2,0,0.4c0.1,0.9-0.6,1.7-1.5,1.8C20.2,16.6,20.1,16.6,20.1,16.6z M20.1,14.1c-0.4,0-0.7,0.3-0.7,0.7c0,0,0,0.1,0,0.1
		c-0.1,0.4,0.2,0.8,0.6,0.8c0.4,0.1,0.8-0.2,0.8-0.6c0-0.1,0-0.2,0-0.2C20.8,14.5,20.6,14.2,20.1,14.1
		C20.2,14.1,20.1,14.1,20.1,14.1z"
                                    />
                                    <path
                                      className="st0"
                                      d="M1.4,14.8H0.5c-0.3,0-0.5-0.2-0.5-0.4c0,0,0,0,0-0.1v-1.7c0-0.3,0.2-0.5,0.4-0.5c0,0,0,0,0.1,0h0.9
		c0.3,0,0.5,0.2,0.5,0.5s-0.2,0.5-0.5,0.5H1v0.7h0.4c0.3,0,0.5,0.2,0.5,0.5S1.7,14.8,1.4,14.8z"
                                    />
                                  </g>
                                </svg>
                                <p className="m-0 leading-none pt-1 font-12">
                                  Envíos gratis en todo México desde $599 pesos
                                </p>
                              </div>
                            </div>
                            {item.featuredImage && (
                              <Image
                                src={item.featuredImage.asset.url}
                                height={300}
                                width={400}
                                alt={item.title}
                                className="hero_featured_image"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })
            : ''}
        </div>
      </div>
      {hero.length > 1 ? (
        <div className="flex justify-center pt-2">
          <div className="embla__dots flex">
            {scrollSnaps.map((_, index) => (
              <button
                className={`bg-purple-120 w-10 rounded-full h-2 ml-1 ease-in 
                    ${index === selectedIndex ? 'w-10' : 'w-2 opacity-50'}
                `}
                name="dot"
                key={index}
                selected={index === selectedIndex}
                onClick={() => scrollTo(index)}
              />
            ))}
          </div>
        </div>
      ) : (
        ''
      )}
      {banner && (
        <div
          className="librero_carousel container flex justify-center mx-auto xs:w-full overflow-hidden"
          ref={emblaRefBanner}
        >
          <div className="home_cards embla__container mt-5 md:mt-11 w-full gap-3 xs:gap-5"
            style={{display: 'flex'}}
          >
            {banner
              ? banner.map((item) => {
                  return (
                    <Link
                      className="hero__banner__slide min-w-xs relative overflow-hidden"
                      key={item._key}
                      to={getPath(item, 'jerp')}
                      style={{
                        minWidth: '318.75px',
                      }}
                      alt={item.title}
                    >
                      <h3 className="text-lg text-black font-semibold z-10 sm:hidden hidden xs:mb-2">
                        {item.title}
                      </h3>
                      <div
                        className="home_banner_card_image justify-start xs:p-3 p-5 overflow-hidden relative rounded-lg items-center content-center flex"
                       
                      >
                        <img 
                          src={     
                            responsiveImage.isHeroMobile(item.backgroundImage.asset.url, 'fm=webp&h=200&w=315')
                          }
                          style={{
                            minHeight: '160px',
                          }}
                          alt={`Banner Image ${item.title}`}
                          className='absolute top-0 left-0 z-0'
                          fetchpriority="eager"
                          />
                        <h3 className="w-7/12 top-1/4 text-white z-10">
                          {item.title}
                        </h3>
                        {item.featuredImage && (
                          <Image
                            src={
                              responsiveImage.isHeroMobile(item.featuredImage.asset.url, 'fm=webp&h=200&w=315')
                            }
                            height={267}
                            width={267}
                            alt={item.title}
                            altText={item.title}
                            className="absolute right-0 bottom-0 z-0"
                          />
                        )}
                      </div>
                    </Link>
                  );
                })
              : ''}
          </div>
        </div>
      )}
    </section>
  );
}